class MoneyFormatter {
  constructor() {
    this.moneyJson = JSON.parse(document.querySelector("[data-money-formatter]").textContent);
  }

  formatMoney(amount) {
    return this.moneyJson.money_format.replace("{{amount}}", (amount / 100.0).toFixed(2));
  }
}

const moneyFormatter = new MoneyFormatter();

class ConvxScroller {
  constructor(root) {
    this.root = root;
    this.panel = this.root.querySelector("[data-convx-scroller-panel]");
    this.previousButton = this.root.querySelector("[data-convx-scroller-previous-button]");
    this.nextButton = this.root.querySelector("[data-convx-scroller-next-button]");

    this.previousButton.addEventListener("click", this.onPreviousButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onNextButtonClick.bind(this));
    this.panel.addEventListener("scroll", this.onPanelScroll.bind(this));
    window.addEventListener("resize", this.onWindowResize.bind(this));
    this.updateEdgeReached();
  }

  onPreviousButtonClick() {
    this.panel.scrollTo({
      left: this.panel.scrollLeft - this.panel.clientWidth * 2 / 3,
      behavior: "smooth",
    });
  }

  onNextButtonClick() {
    this.panel.scrollTo({
      left: this.panel.scrollLeft + this.panel.clientWidth * 2 / 3,
      behavior: "smooth",
    });
  }

  updateEdgeReached() {
    if (this.panel.scrollLeft === 0) {
      this.root.setAttribute("data-convx-scroller-edge-reached-left", "");
    } else {
      this.root.removeAttribute("data-convx-scroller-edge-reached-left");
    }

    if (this.panel.scrollLeft + this.panel.clientWidth >= this.panel.scrollWidth) {
      this.root.setAttribute("data-convx-scroller-edge-reached-right", "");
    } else {
      this.root.removeAttribute("data-convx-scroller-edge-reached-right");
    }
  }

  onPanelScroll() {
    this.updateEdgeReached();
  }

  onWindowResize() {
    this.updateEdgeReached();
  }
}

class ConvxModal {
  constructor(root, triggerButton) {
    this.root = root;
    this.triggerButton = triggerButton;
    this.dialog = this.root.querySelector(`[role="dialog"]`);

    this.root.querySelectorAll("[data-convx-modal-close-button]").forEach(button => {
      button.addEventListener("click", this.onCloseButtonClick.bind(this));
    });

    this.root.addEventListener("click", this.onRootClick.bind(this));
    document.addEventListener("keydown", this.onDocumentKeydown.bind(this));
  }

  onCloseButtonClick() {
    this.hide();
  }

  onRootClick(event) {
    if (event.target === this.root) {
      this.hide();
    }
  }

  onDocumentKeydown(event) {
    if (event.key === "Escape" && !this.root.hidden) {
      this.hide();
    }
  }

  show() {
    document.documentElement.classList.add("convx-modal--not-scrollable");
    this.root.hidden = false;
    this.dialog.focus();
  }

  hide() {
    document.documentElement.classList.remove("convx-modal--not-scrollable");
    this.root.hidden = true;
    this.triggerButton.focus();
  }
}

class ConvxGallery {
  constructor(root) {
    this.root = root;
    this.imageContainer = this.root.querySelector("[data-convx-gallery-image-container]");
    this.dots = Array.from(this.root.querySelectorAll("[data-convx-gallery-dot]"));

    this.dots.forEach((dot, i) => {
      dot.addEventListener("click", this.onDotClick.bind(this, i));
    });

    this.intersectionObserver = new IntersectionObserver(this.imageIntersected.bind(this), {
      root: this.imageContainer,
      threshold: 0.5,
    });

    for (const image of this.imageContainer.children) {
      this.intersectionObserver.observe(image);
    }
  }

  destruct() {
    this.intersectionObserver.disconnect();
    this.intersectionObserver = null;
    this.dots = null;
    this.imageContainer = null;
    this.root = null;
  }

  onDotClick(index) {
    this.goToIndex(index);
  }

  imageIntersected(entries) {
    entries.forEach(entry => {
      if (entry.intersectionRatio > 0.5) {
        for (let i = 0; i < this.imageContainer.children.length; ++i) {
          if (this.imageContainer.children[i] === entry.target) {
            this.dots.forEach(x => { x.removeAttribute("data-convx-gallery-dot-selected"); });
            this.dots[i].setAttribute("data-convx-gallery-dot-selected", "");
            break;
          }
        }
      }
    });
  }

  goToIndex(index) {
    const image = this.imageContainer.children[index];
    this.imageContainer.scrollTo({
      left: image.offsetLeft,
      behavior: "smooth",
    });
  }
}

class UpdateCart {
  constructor(root, key) {
    this.root = root;
    this.key = key;
    this.lineItemProperties = new Map(JSON.parse(this.root.querySelector("[data-update-cart-line-item-properties]").textContent));
    this.popUp = {
      root: null,
      product: null,
      selectedVariant: null,
      gallery: null,
      compareAtPrice: null,
      compareAtPriceData: null,
      price: null,
      priceData: null,
      updateItemButton: null,
      viewProductLink: null,
      optionRadios: null,
      propertyInputs: null,
      propertyRadios: null,
      scrollers: null,
      conditionalShownElements: null,
      conditionalRequiredElements: null,
      imagePropertyControls: null,
      imagePropertySection: null,
      imagePropertyInput: null,
      imagePropertyImage: null,
      balloonNumberInput: null,
      propertyNoStocks: null,
    };
    this.root.addEventListener("click", this.onEditButtonClick.bind(this));
  }

  async onEditButtonClick() {
    this.root.disabled = true;

    if (!this.popUp.root) {
      await this.fetchPopUp();
    }

    this.popUp.root.convxModal.show();
    this.renderSelectedVariant();
    this.root.disabled = false;
  }

  async fetchPopUp() {
    // Setup object property
    const response = await fetch(`${window.Shopify.routes.root}products/${this.root.dataset.updateCartLineItemProductHandle}?variant=${this.root.dataset.updateCartLineItemVariantId}&section_id=update-cart`);
    const text = await response.text();
    const div = document.createElement("div");
    div.innerHTML = text;
    this.popUp.root = div.children[0];
    this.popUp.root.removeAttribute("id");
    this.popUp.root.className = "convx-modal__backdrop";
    this.popUp.root.setAttribute("data-convx-modal", "");
    this.popUp.root.hidden = true;
    this.popUp.root.convxModal = new ConvxModal(this.popUp.root, this.root);
    this.popUp.product = JSON.parse(this.popUp.root.querySelector("[data-update-cart-product-json]").textContent);
    this.popUp.gallery = this.popUp.root.querySelector("[data-convx-gallery]");
    this.popUp.gallery.convxGallery = new ConvxGallery(this.popUp.gallery);
    this.popUp.compareAtPrice = this.popUp.root.querySelector("[data-update-cart-compare-at-price]");
    this.popUp.compareAtPriceData = this.popUp.root.querySelector("[data-update-cart-compare-at-price-data]");
    this.popUp.price = this.popUp.root.querySelector("[data-update-cart-price]");
    this.popUp.priceData = this.popUp.root.querySelector("[data-update-cart-price-data]");
    this.popUp.updateItemButton = this.popUp.root.querySelector("[data-update-cart-update-item-button]");
    this.popUp.updateItemButton.addEventListener("click", this.onUpdateItemButtonClick.bind(this));
    this.popUp.viewProductLink = this.popUp.root.querySelector("[data-update-cart-view-product-link]");
    this.popUp.optionRadios = Array.from(this.popUp.root.querySelectorAll("[data-update-cart-option-radio]"));
    this.popUp.optionRadios.forEach(radio => {
      this.updateInputKey(radio);
      radio.addEventListener("change", this.onOptionChange.bind(this));
    });
    this.popUp.propertyInputs = Array.from(this.popUp.root.querySelectorAll("[data-update-cart-property-input]"));
    this.popUp.propertyInputs.forEach(input => {
      this.updateInputKey(input);
    });
    this.popUp.propertyRadios = Array.from(this.popUp.root.querySelectorAll("[data-update-cart-property-radio]"));
    this.popUp.propertyRadios.forEach(radio => {
      this.updateInputKey(radio);
    });
    this.popUp.scrollers = Array.from(this.popUp.root.querySelectorAll("[data-convx-scroller]"));
    this.popUp.scrollers.forEach(scroller => {
      scroller.convxScroller = new ConvxScroller(scroller);
    });
    this.popUp.conditionalShownElements = Array.from(this.popUp.root.querySelectorAll("[data-update-cart-show-if-selected-options-contain]"));
    this.popUp.conditionalShownElements.forEach(element => {
      const condition = element.dataset.updateCartShowIfSelectedOptionsContain;

      if (condition.includes("||")) {
        element.options = condition.split("||");
        element.optionMatchMode = "||";
      } else {
        element.options = condition.split("&&");
        element.optionMatchMode = "&&";
      }
    });
    this.popUp.conditionalRequiredElements = Array.from(this.popUp.root.querySelectorAll("[data-update-cart-required-if-selected-options-contain]"));
    this.popUp.conditionalRequiredElements.forEach(element => {
      const condition = element.dataset.updateCartRequiredIfSelectedOptionsContain;

      if (condition.includes("||")) {
        element.options = condition.split("||");
        element.optionMatchMode = "||";
      } else {
        element.options = condition.split("&&");
        element.optionMatchMode = "&&";
      }
    });
    this.popUp.imagePropertyControls = Array.from(this.popUp.root.querySelectorAll("[data-update-cart-image-property-control]"));
    this.popUp.imagePropertyControls.forEach(radio => {
      this.updateInputKey(radio);
      radio.addEventListener("change", this.onImagePropertyControlChange.bind(this));
    });
    this.popUp.imagePropertySection = this.popUp.root.querySelector("[data-update-cart-image-property-section]");
    this.popUp.imagePropertyInput = this.popUp.root.querySelector("[data-update-cart-image-property-input]");
    if (this.popUp.imagePropertyInput) {
      this.updateInputKey(this.popUp.imagePropertyInput);;
      this.popUp.imagePropertyInput.addEventListener("change", this.onImagePropertyInputChange.bind(this));
    }
    this.popUp.imagePropertyImage = this.popUp.root.querySelector("[data-update-cart-image-property-image]");
    this.popUp.balloonNumberInput = this.popUp.root.querySelector("[data-update-cart-balloon-number-input]");
    this.popUp.propertyNoStocks = Array.from(this.popUp.root.querySelectorAll("[data-update-cart-property-no-stock]"));
    // End setup object property

    document.body.append(this.popUp.root);

    // Setup input value
    this.updateSelectedVariant();

    this.popUp.propertyInputs.forEach(input => {
      const key = input.name.slice(11, -1);
      const value = this.lineItemProperties.get(key);

      if (value) {
        input.value = value;
      }
    });

    this.popUp.propertyRadios.forEach(radio => {
      const key = radio.dataset.updateCartPropertyName.slice(11, -1);
      const value = this.lineItemProperties.get(key);

      if (value && (radio.value === value || escapeHtml(radio.value) === value)) {
        radio.checked = true;
      }
    });

    if (this.popUp.imagePropertyInput) {
      const key = this.popUp.imagePropertyInput.name.slice(11, -1);
      const value = this.lineItemProperties.get(key);

      if (value) {
        this.popUp.imagePropertyImage.src = value;
        this.popUp.imagePropertyImage.hidden = false;
        this.popUp.imagePropertySection.hidden = false;

        for (const radio of this.popUp.imagePropertyControls) {
          if (radio.value === "Free 3R Photo") {
            radio.checked = true;
            break;
          }
        }
      } else {
        this.popUp.imagePropertyImage.src = "";
        this.popUp.imagePropertyImage.hidden = true;

        if (this.popUp.imagePropertyControls.length) {
          this.popUp.imagePropertySection.hidden = true;
        }

        for (const radio of this.popUp.imagePropertyControls) {
          if (radio.value === "No Photo") {
            radio.checked = true;
            break;
          }
        }
      }
    }
    // End setup input value
  }

  updateSelectedVariant() {
    const checkedOptions = this.popUp.optionRadios.filter(radio => radio.checked).map(radio => radio.value);
    this.popUp.selectedVariant = this.popUp.product.variants.find(x => arrayEquals(x.options, checkedOptions));
  }

  renderSelectedVariant() {
    if (this.popUp.selectedVariant.featured_image) {
      const variantImageIndex = this.popUp.product.images.indexOf(this.popUp.selectedVariant.featured_image.src);

      if (variantImageIndex > -1) {
        this.popUp.gallery.convxGallery.goToIndex(variantImageIndex);
      }
    }

    if (this.popUp.selectedVariant.compare_at_price && this.popUp.selectedVariant.compare_at_price > this.popUp.selectedVariant.price) {
      this.popUp.compareAtPrice.hidden = false;
      this.popUp.compareAtPriceData.textContent = moneyFormatter.formatMoney(this.popUp.selectedVariant.compare_at_price);
      this.popUp.price.classList.add("update-cart__info-price--on-sale");
    } else {
      this.popUp.compareAtPrice.hidden = true;
      this.popUp.compareAtPriceData.textContent = "";
      this.popUp.price.classList.remove("update-cart__info-price--on-sale");
    }

    this.popUp.priceData.textContent = moneyFormatter.formatMoney(this.popUp.selectedVariant.price);

    for (let i = 0, j = 0; i < this.popUp.selectedVariant.options.length; ++i) {
      const optionName = this.popUp.product.options[i];
      const selectedValue = this.popUp.selectedVariant.options[i];

      for (; j < this.popUp.optionRadios.length && this.popUp.optionRadios[j].dataset.updateCartOptionName === optionName; ++j) {
        if (this.popUp.optionRadios[j].value === selectedValue) {
          this.popUp.optionRadios[j].checked = true;
        }
      }
    }

    this.popUp.viewProductLink.href = `${window.Shopify.routes.root}products/${this.popUp.product.handle}?variant=${this.popUp.selectedVariant.id}`;

    this.popUp.conditionalShownElements.forEach(element => {
      if (element.optionMatchMode == "||") {
        element.hidden = !this.popUp.selectedVariant.options.some(x => element.options.includes(x));
      } else {
        element.hidden = !element.options.every(x => this.popUp.selectedVariant.options.includes(x));
      }
    });

    this.popUp.conditionalRequiredElements.forEach(element => {
      if (element.optionMatchMode == "||") {
        element.required = this.popUp.selectedVariant.options.some(x => element.options.includes(x));
      } else {
        element.required = element.options.every(x => this.popUp.selectedVariant.options.includes(x));
      }
    });

    if (this.popUp.balloonNumberInput) {
      if (this.popUp.selectedVariant.options.includes("Single")) {
        this.popUp.balloonNumberInput.pattern = "[0-9]";
        this.popUp.balloonNumberInput.placeholder = "Insert 1 digit";
      } else {
        this.popUp.balloonNumberInput.pattern = "[0-9][0-9]";
        this.popUp.balloonNumberInput.placeholder = "Insert 2 digits";
      }
    }

    const containsNoStockProperty = this.popUp.propertyNoStocks.some(x => !x.hidden);

    this.popUp.scrollers.forEach(scroller => {
      scroller.convxScroller.updateEdgeReached();
    });

    if (this.popUp.selectedVariant.available && !containsNoStockProperty) {
      this.popUp.updateItemButton.textContent = "Update Item";
      this.popUp.updateItemButton.disabled = false;
    } else {
      this.popUp.updateItemButton.textContent = this.popUp.updateItemButton.dataset.updateCartSoldOutText;
      this.popUp.updateItemButton.disabled = true;
    }
  }

  async onUpdateItemButtonClick() {
    this.popUp.updateItemButton.disabled = true;

    let errorMessages = [];

    this.popUp.propertyInputs.forEach(input => {
      if (!input.reportValidity()) {
        errorMessages.push(updateCartGetErrorMessage(input));
      }
    });

    const validatedNames = new Set();

    this.popUp.propertyRadios.forEach(radio => {
      if (!validatedNames.has(radio.name)) {
        validatedNames.add(radio.name);

        if (!radio.reportValidity()) {
          errorMessages.push(updateCartGetErrorMessage(radio));
        }
      }
    });

    if (this.popUp.imagePropertyImage) {
      if (this.popUp.imagePropertyControls.some(x => x.checked && x.value === "No Photo")) {
        this.popUp.imagePropertyInput.required = false;
      } else if (this.popUp.imagePropertyImage.hidden) {
        this.popUp.imagePropertyInput.required = true;
        this.popUp.imagePropertyInput.reportValidity();
        errorMessages.push(updateCartGetErrorMessage(this.popUp.imagePropertyInput));
      }
    }

    if (errorMessages.length) {
      alert(`Error:\n- ${errorMessages.join("\n- ")}`);
      this.popUp.updateItemButton.disabled = false;
      return;
    }

    const properties = {};

    this.popUp.propertyInputs.forEach(input => {
      if (input.required) {
        const key = input.name.slice(11, -1);
        properties[key] = input.value;
      }
    });

    this.popUp.propertyRadios.forEach(radio => {
      if (radio.required && radio.checked) {
        const key = radio.dataset.updateCartPropertyName.slice(11, -1);
        properties[key] = radio.value;
      }
    });

    if (this.popUp.imagePropertyImage) {
      if (this.popUp.imagePropertyControls.some(x => x.checked && x.value === "No Photo")) {
        // Do nothing
      } else if (this.popUp.imagePropertyImage.hidden) {
        // Do nothing
      } else {
        const key = this.popUp.imagePropertyInput.name.slice(11, -1);
        properties[key] = this.popUp.imagePropertyImage.src;
      }
    }

    await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: "post",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        "id": this.root.dataset.updateCartLineItemKey,
        "quantity": 0,
      }),
    });

    await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "post",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        "items": [{
          "id": this.popUp.selectedVariant.id,
          "quantity": 1,
          properties,
        }]
      })
    });

    window.location.href = `${window.Shopify.routes.root}cart`;
  }

  onOptionChange() {
    this.updateSelectedVariant();
    this.renderSelectedVariant();
  }

  updateInputKey(input) {
    const label = this.popUp.root.querySelector(`[for="${input.id}"]`);

    if (label) {
      label.htmlFor = label.htmlFor.replace("{key}", this.key);
    }

    input.id = input.id.replace("{key}", this.key);
    input.name = input.name.replace("{key}", this.key);
  }

  async onImagePropertyInputChange() {
    const files = this.popUp.imagePropertyInput.files;

    if (files.length === 0) {
      this.popUp.imagePropertyImage.src = "";
      this.popUp.imagePropertyImage.hidden = true;
      return;
    }

    const file = files[0];

    const response = await fetch("https://ov9xqk2ey8.execute-api.ap-southeast-1.amazonaws.com/default/bt_uploadurl", {
      method: "post",
      body: JSON.stringify({
        "file_origin": "photo_",
        "file_type": file.type,
      }),
    });

    const responseJson = await response.json();

    const uploadResponse = await fetch(responseJson.uploadURL, {
      method: "put",
      headers: {
        "content-type": file.type,
      },
      body: file,
    });

    this.popUp.imagePropertyImage.src = `https://bloomthis-images.s3.ap-southeast-1.amazonaws.com/${responseJson.photoFilename}`;
    this.popUp.imagePropertyImage.hidden = false;
  }

  onImagePropertyControlChange() {
    let wantPhoto = true;

    for (const radio of this.popUp.imagePropertyControls) {
      if (radio.checked && radio.value === "No Photo") {
        wantPhoto = false;
        break;
      }
    }

    this.popUp.imagePropertySection.hidden = !wantPhoto;
  }
}

function arrayEquals(a1, a2) {
  return a1.length === a2.length && a1.every((x, i) => x === a2[i]);
}

function updateCartGetErrorMessage(input) {
  let key = null;

  if (input.type === "radio") {
    key = input.dataset.updateCartPropertyName.slice(11, -1);
  } else {
    key = input.name.slice(11, -1);
  }

  let validationMessage = input.validationMessage;

  if (input.validity.patternMismatch) {
    if (input.pattern === "[0-9]") {
      validationMessage = "Should be exactly 1 digit."
    } else if (input.pattern === "[0-9][0-9]") {
      validationMessage = "Should be exactly 2 digits."
    }
  }

  return `${key}: ${validationMessage}`;
}

function escapeHtml(unsafe) {
  return unsafe.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll(`"`, "&quot;").replaceAll("'", "&#39;"); 
}

function main() {
  document.querySelectorAll("[data-update-cart]").forEach((element, i) => {
    element.updateCart = new UpdateCart(element, (i + 1).toString());
  });
}

main();
